import React from 'react'
import type { ImageContext, StockPlatform } from '../contracts/metadataGenerator'
import { LocationAutocomplete } from './LocationAutocomplete'
import { AppIcons } from './icons'

interface ContextFormProps {
  context: ImageContext
  onChange: (context: ImageContext) => void
}

export function ContextForm({ context, onChange }: ContextFormProps) {

  const handleChange = (field: keyof ImageContext, value: string | number | Date | StockPlatform | undefined) => {
    onChange({
      ...context,
      [field]: value === '' ? undefined : value,
    })
  }


  const hasContext = context.location || context.title || context.date || context.month || context.year || context.platform

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-4 lg:mb-6">
      <div className="p-4 lg:p-6">
        <div className="flex items-center space-x-3 mb-3 lg:mb-4">
          <div className={`w-3 h-3 rounded-full ${hasContext ? 'bg-blue-500' : 'bg-gray-400'}`} />
          <h2 className="text-base lg:text-lg font-semibold text-gray-900">
            Context
          </h2>
          {hasContext && (
            <span className="text-xs lg:text-sm px-2 py-1 rounded-full bg-blue-100 text-blue-800">
              Added
            </span>
          )}
        </div>

        <div className="space-y-4 lg:space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Target Platform <span className="text-red-500">*</span>
              </label>
              <select
                value={context.platform || ''}
                onChange={(e) => handleChange('platform', (e.target.value as StockPlatform) || undefined)}
                className="w-full p-3 lg:p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
                style={{ minHeight: '44px' }}
                required
              >
                <option value="">Select Platform</option>
                <option value="shutterstock">Shutterstock</option>
                <option value="adobe-stock">Adobe Stock</option>
                <option value="getty-images">Getty Images</option>
                <option value="istock">iStock</option>
              </select>
              <p className="text-xs lg:text-xs text-gray-500 mt-1">
                Choose your target platform for category-specific optimization
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Location
              </label>
              <div style={{ minHeight: '44px' }}>
                <LocationAutocomplete
                  value={context.location || ''}
                  onChange={(value) => handleChange('location', value)}
                  placeholder="e.g., New York City, Paris, Tokyo"
                />
              </div>
              <p className="text-xs lg:text-xs text-gray-500 mt-1">
                City, country, or specific location
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Event/Title Context
              </label>
              <input
                type="text"
                value={context.title || ''}
                onChange={(e) => handleChange('title', e.target.value)}
                placeholder="e.g., Tech Conference, Street Festival, Architecture"
                className="w-full p-3 lg:p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
                style={{ minHeight: '44px' }}
              />
              <p className="text-xs lg:text-xs text-gray-500 mt-1">
                Event, theme, or subject context
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date Context
              </label>
              <input
                type="date"
                value={context.date ? (context.date instanceof Date ? context.date.toISOString().split('T')[0] : new Date(context.date).toISOString().split('T')[0]) : ''}
                onChange={(e) => handleChange('date', e.target.value ? new Date(e.target.value) : undefined)}
                className="w-full p-3 lg:p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
                style={{ minHeight: '44px' }}
                max={new Date().toISOString().split('T')[0]}
              />
              <p className="text-xs lg:text-xs text-gray-500 mt-1">
                Full date for editorial content (Month Day, Year format for Shutterstock)
              </p>
            </div>
        </div>

        {hasContext && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 mt-4">
            <h4 className="font-medium text-green-900 mb-1 flex items-center">
              <AppIcons.CheckCircle className="w-4 h-4 mr-2 text-green-600" />
              Context Active
            </h4>
            <p className="text-sm text-green-800">
              AI will use this context to generate more accurate metadata with location, date, and thematic information.
            </p>
          </div>
        )}

        <div className="flex justify-end mt-4">
          <button
            onClick={() => onChange({})}
            className="text-red-600 hover:text-red-800 text-sm font-medium px-4 py-2 rounded-lg hover:bg-red-50 transition-colors"
            style={{ minHeight: '44px' }}
          >
            Clear All Context
          </button>
        </div>
      </div>
    </div>
  )
}